import React, { useState, useRef, useEffect, useCallback } from "react";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { BrowserMultiFormatReader } from "@zxing/browser";
import {
  BarcodeFormat,
  DataMatrixDefaultPlacement,
  DataMatrixErrorCorrection,
  DataMatrixSymbolInfo,
  DataMatrixWriter,
  DecodeHintType,
} from "@zxing/library";
import { useBotCheck } from "./FreeFunctionsPage";
import "./BarcodeLabeler.css";

// ─── constants ───────────────────────────────────────────────────────────────

const LABEL_SIZES = [
  // Стандартные (landscape, с текстом)
  { id: "58x40",  label: "58 × 40 мм",  width: "58mm",  height: "40mm"  },
  { id: "58x30",  label: "58 × 30 мм",  width: "58mm",  height: "30mm"  },
  { id: "40x25",  label: "40 × 25 мм",  width: "40mm",  height: "25mm"  },
  { id: "100x50", label: "100 × 50 мм", width: "100mm", height: "50mm"  },
  { id: "120x75", label: "120 × 75 мм", width: "120mm", height: "75mm"  },
  // QR-only (малые квадраты)
  { id: "15x15",  label: "15 × 15 мм",  width: "15mm",  height: "15mm"  },
  { id: "20x20",  label: "20 × 20 мм",  width: "20mm",  height: "20mm"  },
  { id: "25x25",  label: "25 × 25 мм",  width: "25mm",  height: "25mm"  },
  // Можно с текстом (portrait/square)
  { id: "20x30",  label: "20 × 30 мм",  width: "20mm",  height: "30mm"  },
  { id: "30x30",  label: "30 × 30 мм",  width: "30mm",  height: "30mm"  },
  { id: "40x40",  label: "40 × 40 мм",  width: "40mm",  height: "40mm"  },
  { id: "20x40",  label: "20 × 40 мм",  width: "20mm",  height: "40mm"  },
  { id: "30x40",  label: "30 × 40 мм",  width: "30mm",  height: "40mm"  },
  { id: "30x58",  label: "30 × 58 мм",  width: "30mm",  height: "58mm"  },
];

const DEFAULT_SIZE = LABEL_SIZES.find((s) => s.id === "58x40");

const MAIN_FORMATS = [
  { id: "qr",      label: "QR-код",   group: "2D" },
  { id: "CODE128", label: "Code 128", group: "1D" },
  { id: "EAN13",   label: "EAN-13",   group: "1D" },
];
const OTHER_FORMATS = [
  { id: "EAN8",   label: "EAN-8",   group: "1D" },
  { id: "UPCA",   label: "UPC-A",   group: "1D" },
  { id: "UPCE",   label: "UPC-E",   group: "1D" },
  { id: "CODE39", label: "Code 39", group: "1D" },
  { id: "ITF14",  label: "ITF-14",  group: "1D" },
];
const ALL_FORMATS = [...MAIN_FORMATS, ...OTHER_FORMATS];

const FONTS = [
  { id: "Arial",           label: "Arial"           },
  { id: "Verdana",         label: "Verdana"         },
  { id: "Trebuchet MS",    label: "Trebuchet MS"    },
  { id: "Georgia",         label: "Georgia"         },
  { id: "Times New Roman", label: "Times New Roman" },
  { id: "Courier New",     label: "Courier New"     },
];

const SHELF_UNITS = [
  { id: "days",   label: "дней"    },
  { id: "months", label: "месяцев" },
  { id: "years",  label: "лет"     },
];

const SCANNER_THRESHOLD_MS = 80;
const SCANNER_MIN_LENGTH   = 4;
const SCANNER_FINALIZE_DELAY_MS = 140;
const PX_PER_MM            = 3.78; // 96dpi
const LABEL_INSET_MM       = 0.5;  // mm on each side; working area is label size minus 1mm
const CODE_GRID_FR         = 2;
const TEXT_GRID_FR         = 1;
const GRID_TOTAL_FR        = CODE_GRID_FR + TEXT_GRID_FR;
const GS                   = String.fromCharCode(29);
const DATA_MATRIX_FORMAT   = "gs1datamatrix";
const DATA_MATRIX_PAD      = 129;

// USB scanner on Windows with Russian keyboard layout sends Cyrillic instead of Latin.
// Map each Cyrillic key back to its positional US-QWERTY equivalent.
const RU_TO_LATIN_MAP = Object.fromEntries([
  ...'йцукенгшщзхъ'.split('').map((c, i) => [c, 'qwertyuiop[]'[i]]),
  ...'фывапролджэ'.split('').map((c, i)  => [c, "asdfghjkl;'"[i]]),
  ...'ячсмитьбю'.split('').map((c, i)    => [c, 'zxcvbnm,.'[i]]),
  ...'ЙЦУКЕНГШЩЗХЪ'.split('').map((c, i) => [c, 'QWERTYUIOP{}'[i]]),
  ...'ФЫВАПРОЛДЖЭ'.split('').map((c, i)  => [c, 'ASDFGHJKL:"'[i]]),
  ...'ЯЧСМИТЬБЮ'.split('').map((c, i)    => [c, 'ZXCVBNM<>'[i]]),
]);

// GS1 AIs with fixed data length — no GS separator needed after them.
const GS1_FIXED_AIs = new Set([
  '00','01','02','03','04',
  '11','12','13','14','15','16','17','18','19','20',
]);
const CAMERA_FORMATS = [
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
];

// ─── date helpers ─────────────────────────────────────────────────────────────

const todayStr = () => new Date().toISOString().slice(0, 10);

function addDuration(dateStr, value, unit) {
  if (!dateStr || !value) return "";
  const n = parseInt(value);
  if (isNaN(n) || n <= 0) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  if (unit === "days")   d.setDate(d.getDate() + n);
  if (unit === "months") d.setMonth(d.getMonth() + n);
  if (unit === "years")  d.setFullYear(d.getFullYear() + n);
  return d.toISOString().slice(0, 10);
}

function diffAsDuration(d1Str, d2Str) {
  if (!d1Str || !d2Str) return null;
  const d1 = new Date(d1Str), d2 = new Date(d2Str);
  if (isNaN(d1) || isNaN(d2) || d2 <= d1) return null;
  const years = d2.getFullYear() - d1.getFullYear();
  if (years > 0) {
    const chk = new Date(d1); chk.setFullYear(chk.getFullYear() + years);
    if (chk.getTime() === d2.getTime()) return { value: String(years), unit: "years" };
  }
  const months = (d2.getFullYear() - d1.getFullYear()) * 12 + d2.getMonth() - d1.getMonth();
  if (months > 0) {
    const chk = new Date(d1); chk.setMonth(chk.getMonth() + months);
    if (chk.getTime() === d2.getTime()) return { value: String(months), unit: "months" };
  }
  return { value: String(Math.round((d2 - d1) / 86400000)), unit: "days" };
}

const shelfLabel = (value, unit) => {
  if (!value) return "";
  return `${value} ${{ days: "дн.", months: "мес.", years: "лет" }[unit] || unit}`;
};
const fmtDate = (s) => (s || "").split("-").reverse().join(".");

// ─── layout helpers ────────────────────────────────────────────────────────────

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function textLineCount(hasTitle, fields, values) {
  let count = hasTitle ? 1 : 0;
  if (fields.mfg && values.mfg) count += 2;
  if (fields.shelf && values.shelf.value) count += 2;
  if (fields.exp && values.exp) count += 2;
  return Math.max(1, count);
}

function fontSizeFromBox(widthMm, heightMm, lines) {
  const byHeight = (heightMm / Math.max(1, lines)) * 2.15;
  const byWidth = widthMm * 1.8;
  return clamp(Math.min(byHeight, byWidth), 3.2, 9);
}

function gridParams(size, format, hasText, lineCount = 1) {
  const w = parseFloat(size.width);
  const h = parseFloat(size.height);
  const workW = Math.max(1, w - LABEL_INSET_MM * 2);
  const workH = Math.max(1, h - LABEL_INSET_MM * 2);
  const horiz = w >= h;
  const codeW = hasText && horiz ? workW * CODE_GRID_FR / GRID_TOTAL_FR : workW;
  const codeH = hasText && !horiz ? workH * CODE_GRID_FR / GRID_TOTAL_FR : workH;
  const textW = hasText ? (horiz ? workW * TEXT_GRID_FR / GRID_TOTAL_FR : workW) : 0;
  const textH = hasText ? (horiz ? workH : workH * TEXT_GRID_FR / GRID_TOTAL_FR) : 0;
  const codeSizeMm = Math.max(1, Math.min(codeW, codeH));
  const barcodeMm = Math.max(6, Math.min(codeH, hasText ? codeH * 0.58 : codeH * 0.72));
  const fs = fontSizeFromBox(textW || workW, textH || workH, lineCount);

  return {
    w, h, workW, workH, codeW, codeH, textW, textH,
    fs,
    titleFs: clamp(fs * 1.08, 3.6, 10),
    qrMm: codeSizeMm,
    qrPx: Math.max(200, Math.round(codeSizeMm * PX_PER_MM * 4)),
    barcodeH: Math.max(20, Math.round(barcodeMm * PX_PER_MM)),
    pad: LABEL_INSET_MM,
    horiz,
    noText: !hasText,
  };
}

function is2dFormat(format) {
  return format === "qr" || format === DATA_MATRIX_FORMAT;
}

function normalizeCodeValue(raw, format) {
  if (format !== DATA_MATRIX_FORMAT) return raw;

  let val = raw
    .replace(/^\]d[12]/i, "")
    .replace(/<GS>|\[GS\]|\{GS\}|\\u001d|\\x1d|\\x1D|\\035/g, GS);

  // USB scanner with Russian Windows keyboard layout: remap Cyrillic → Latin
  if (/[а-яёА-ЯЁ]/.test(val)) {
    val = val.split('').map(c => RU_TO_LATIN_MAP[c] ?? c).join('');
  }

  // HRI parentheses format from scanner: (01)GTIN(21)SERIAL(91)VK(92)CRYPTO
  // Convert to raw GS1 with GS separators between variable-length AIs.
  if (/^\s*\(\d/.test(val)) {
    const parts = [];
    const rx = /\((\d{2,4})\)([^(]*)/g;
    let m;
    while ((m = rx.exec(val)) !== null) {
      parts.push({ ai: m[1], value: m[2].trim() });
    }
    if (parts.length > 0) {
      val = parts.reduce((acc, { ai, value }, i) => {
        const needsGs = i < parts.length - 1 && !GS1_FIXED_AIs.has(ai);
        return acc + ai + value + (needsGs ? GS : '');
      }, '');
    }
  }

  return val;
}

function drawBitMatrixToCanvas(canvas, matrix, pixelSize) {
  const sourceW = matrix.getWidth();
  const sourceH = matrix.getHeight();
  const quietModules = 2;
  const modulePx = Math.max(1, Math.floor(pixelSize / (Math.max(sourceW, sourceH) + quietModules * 2)));
  const drawW = (sourceW + quietModules * 2) * modulePx;
  const drawH = (sourceH + quietModules * 2) * modulePx;
  const offsetX = Math.floor((pixelSize - drawW) / 2);
  const offsetY = Math.floor((pixelSize - drawH) / 2);
  const ctx = canvas.getContext("2d");

  canvas.width = pixelSize;
  canvas.height = pixelSize;
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, pixelSize, pixelSize);
  ctx.fillStyle = "#000";

  for (let y = 0; y < sourceH; y += 1) {
    for (let x = 0; x < sourceW; x += 1) {
      if (matrix.get(x, y)) {
        ctx.fillRect(offsetX + (x + quietModules) * modulePx, offsetY + (y + quietModules) * modulePx, modulePx, modulePx);
      }
    }
  }
}

function randomizeDataMatrixPad(position) {
  const pseudoRandom = ((149 * position) % 253) + 1;
  const temp = DATA_MATRIX_PAD + pseudoRandom;
  return temp <= 254 ? temp : temp - 254;
}

function encodeGs1DataMatrixAscii(value) {
  let codewords = String.fromCharCode(232);

  for (let i = 0; i < value.length; i += 1) {
    const charCode = value.charCodeAt(i);
    if (i + 1 < value.length && /\d/.test(value[i]) && /\d/.test(value[i + 1])) {
      codewords += String.fromCharCode(130 + Number(value.slice(i, i + 2)));
      i += 1;
      continue;
    }
    if (charCode <= 127) {
      codewords += String.fromCharCode(charCode + 1);
      continue;
    }
    if (charCode <= 255) {
      codewords += String.fromCharCode(235, charCode - 127);
      continue;
    }
    throw new Error("Only ASCII and extended ASCII are supported for GS1 Data Matrix");
  }

  const symbolInfo = DataMatrixSymbolInfo.lookup(codewords.length, 0, null, null, true);
  const capacity = symbolInfo.getDataCapacity();
  if (codewords.length < capacity) {
    codewords += String.fromCharCode(DATA_MATRIX_PAD);
  }
  while (codewords.length < capacity) {
    codewords += String.fromCharCode(randomizeDataMatrixPad(codewords.length + 1));
  }

  return { codewords, symbolInfo };
}

function renderGs1DataMatrix(canvas, rawValue, pixelSize) {
  const value = normalizeCodeValue(rawValue, DATA_MATRIX_FORMAT);
  const { codewords, symbolInfo } = encodeGs1DataMatrixAscii(value);
  const ecc = DataMatrixErrorCorrection.encodeECC200(codewords, symbolInfo);
  const placement = new DataMatrixDefaultPlacement(ecc, symbolInfo.getSymbolDataWidth(), symbolInfo.getSymbolDataHeight());
  placement.place();
  const matrix = new DataMatrixWriter().encodeLowLevel(placement, symbolInfo, 0, 0);
  drawBitMatrixToCanvas(canvas, matrix, pixelSize);
}

function dataMatrixDataUrl(rawValue, pixelSize) {
  const canvas = document.createElement("canvas");
  renderGs1DataMatrix(canvas, rawValue, pixelSize);
  return canvas.toDataURL("image/png");
}

function useCameraScanner(onResult) {
  const [open, setOpen]       = useState(false);
  const [error, setError]     = useState("");
  const [scanned, setScanned] = useState("");
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const videoRef              = useRef(null);
  const controlsRef           = useRef(null);

  const startScan = useCallback(() => {
    setError("");
    setScanned("");
    setOpen(true);
  }, []);

  const stopScan = useCallback(() => {
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch {}
      controlsRef.current = null;
    }
    setOpen(false);
    setScanned("");
  }, []);

  const switchCamera = useCallback(() => {
    if (devices.length < 2) return;
    const currentIndex = Math.max(0, devices.findIndex((d) => d.deviceId === deviceId));
    const next = devices[(currentIndex + 1) % devices.length];
    if (!next) return;
    if (controlsRef.current) {
      try { controlsRef.current.stop(); } catch {}
      controlsRef.current = null;
    }
    setScanned("");
    setError("");
    setDeviceId(next.deviceId);
  }, [devices, deviceId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, CAMERA_FORMATS);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints);

    const init = async () => {
      await new Promise((res) => setTimeout(res, 100));
      if (cancelled || !videoRef.current) return;
      try {
        const videoDevices = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(videoDevices);
        const back = videoDevices.find((d) => /back|rear|environment|зад/i.test(d.label));
        const selectedDeviceId = deviceId || back?.deviceId || videoDevices[0]?.deviceId;
        if (selectedDeviceId && selectedDeviceId !== deviceId) setDeviceId(selectedDeviceId);
        const onDecode = (result) => {
          if (cancelled || !result) return;
          const text = result.getText();
          setScanned(text);
          navigator.vibrate?.(80);
          setTimeout(() => { if (!cancelled) { onResult(text); stopScan(); } }, 400);
        };
        const controls = deviceId
          ? await reader.decodeFromVideoDevice(selectedDeviceId, videoRef.current, onDecode)
          : await reader.decodeFromConstraints(
              { video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 1280 } } },
              videoRef.current,
              onDecode
            );
        if (!cancelled) controlsRef.current = controls;
      } catch (e) {
        if (!cancelled)
          setError(e?.message?.includes("Permission")
            ? "Нет доступа к камере. Разрешите доступ в браузере."
            : "Не удалось открыть камеру.");
      }
    };

    init();
    return () => {
      cancelled = true;
      if (controlsRef.current) { try { controlsRef.current.stop(); } catch {} controlsRef.current = null; }
    };
  }, [open, deviceId, onResult, stopScan]);

  return { open, startScan, stopScan, switchCamera, videoRef, error, scanned, devices, deviceId };
}

// ─── component ────────────────────────────────────────────────────────────────

export default function BarcodeLabeler() {
  const [format,       setFormat]       = useState("CODE128");
  const [font,         setFont]         = useState("Arial");
  const [value,        setValue]        = useState("");
  const [title,        setTitle]        = useState("");
  const [labelSize,    setLabelSize]    = useState(DEFAULT_SIZE);
  const [rotated,      setRotated]      = useState(false);
  const [copies,       setCopies]       = useState(1);
  const [showOther,    setShowOther]    = useState(false);
  const [barcodeError, setBarcodeError] = useState("");

  const [dateFields, setDateFields] = useState({ mfg: false, exp: false, shelf: false });
  const [dateValues, setDateValues] = useState({
    mfg:   todayStr(),
    exp:   todayStr(),
    shelf: { value: "", unit: "months" },
  });
  const [dateManual, setDateManual] = useState({ mfg: true, exp: false, shelf: false });
  const [autoPrint, setAutoPrint] = useState(true);

  const { isBot, HoneypotField } = useBotCheck();

  const canvasRef     = useRef(null);
  const svgRef        = useRef(null);
  const inputRef      = useRef(null);
  const previewBoxRef = useRef(null);
  const scannerBuffer = useRef([]);
  const scannerTimer  = useRef(null);
  const scannerFinalizeTimer = useRef(null);
  const pendingScanPrintRef = useRef(false);

  // Measure actual preview box pixel width for accurate QR sizing
  const [previewBoxW, setPreviewBoxW] = useState(300);
  useEffect(() => {
    if (!previewBoxRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setPreviewBoxW(w);
    });
    ro.observe(previewBoxRef.current);
    return () => ro.disconnect();
  }, []);

  const handleScanResult = useCallback((text) => {
    pendingScanPrintRef.current = autoPrint;
    setValue(text);
    inputRef.current?.focus();
  }, [autoPrint]);

  const camera = useCameraScanner(handleScanResult);

  // ─── derived layout values ─────────────────────────────────────────────────

  // Effective dimensions with rotation support
  const lw  = rotated ? parseFloat(labelSize.height) : parseFloat(labelSize.width);
  const lh  = rotated ? parseFloat(labelSize.width)  : parseFloat(labelSize.height);
  const effectiveW = rotated ? labelSize.height : labelSize.width;
  const effectiveH = rotated ? labelSize.width  : labelSize.height;

  // hasText: any visible text on label
  const hasText = !!(title.trim() || dateFields.mfg || dateFields.shelf || dateFields.exp);
  const textLines = textLineCount(!!title.trim(), dateFields, dateValues);
  const layout = gridParams({ width: effectiveW, height: effectiveH }, format, hasText, textLines);

  // QR preview sizes in exact pixels (computed from measured preview box width)
  // scale = previewBoxW / lw  (px per mm)
  const previewScale = previewBoxW / lw;
  const previewPadPx = Math.max(1, Math.round(LABEL_INSET_MM * previewScale));
  const codePreviewPx = Math.max(20, Math.floor(Math.min(layout.codeW, layout.codeH) * previewScale));
  const barPreviewH = Math.max(24, Math.round((layout.barcodeH / PX_PER_MM) * previewScale));
  const previewTextFs = Math.max(5, Math.round(layout.fs * 1.33));
  const isMatrix = is2dFormat(format);
  const qrNoText = isMatrix && !hasText;
  const qrHoriz = isMatrix && hasText && layout.horiz;
  const qrPortrait = isMatrix && hasText && !layout.horiz;
  const qrNoTextPx = codePreviewPx;
  const qrHorizPx = codePreviewPx;
  const qrPortraitPx = codePreviewPx;

  // ─── date formula ──────────────────────────────────────────────────────────

  useEffect(() => {
    setDateValues((prev) => {
      const next = { ...prev };
      if (dateFields.mfg && dateFields.shelf && dateFields.exp && !dateManual.exp) {
        const calc = addDuration(prev.mfg, prev.shelf.value, prev.shelf.unit);
        if (calc) next.exp = calc;
      } else if (dateFields.mfg && dateFields.exp && dateFields.shelf && !dateManual.shelf) {
        const diff = diffAsDuration(prev.mfg, prev.exp);
        if (diff) next.shelf = diff;
      }
      return next;
    });
  }, [dateValues.mfg, dateValues.exp, dateValues.shelf.value, dateValues.shelf.unit, dateFields, dateManual.exp, dateManual.shelf]);

  const dateInconsistent = (() => {
    if (!dateFields.mfg || !dateFields.exp || !dateFields.shelf) return false;
    if (!dateManual.mfg || !dateManual.exp || !dateManual.shelf) return false;
    const calc = addDuration(dateValues.mfg, dateValues.shelf.value, dateValues.shelf.unit);
    return !!(calc && calc !== dateValues.exp);
  })();

  const handleMfgChange   = (val) => { setDateManual((m) => ({ ...m, mfg: true }));              setDateValues((v) => ({ ...v, mfg: val })); };
  const handleExpChange   = (val) => { setDateManual((m) => ({ ...m, exp: true, shelf: false })); setDateValues((v) => ({ ...v, exp: val })); };
  const handleShelfValChange = (val) => { setDateManual((m) => ({ ...m, shelf: true, exp: false })); setDateValues((v) => ({ ...v, shelf: { ...v.shelf, value: val } })); };
  const handleShelfUnitChange = (unit) => { setDateManual((m) => ({ ...m, shelf: true, exp: false })); setDateValues((v) => ({ ...v, shelf: { ...v.shelf, unit } })); };
  const toggleDateField = (id) => {
    setDateFields((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (!prev[id]) setDateManual((m) => ({ ...m, [id]: false }));
      return next;
    });
  };

  // ─── barcode preview ───────────────────────────────────────────────────────

  const renderBarcode = useCallback((val) => {
    if (!val) return;
    setBarcodeError("");
    if (format === "qr" || format === DATA_MATRIX_FORMAT) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (format === DATA_MATRIX_FORMAT) {
        try {
          renderGs1DataMatrix(canvas, val, 256);
        } catch {
          setBarcodeError("Ошибка генерации Data Matrix ЧЗ");
        }
      } else {
        QRCode.toCanvas(canvas, val, {
          width: 256,
          margin: 1,
          color: { dark: "#000", light: "#fff" },
        }).catch(() => setBarcodeError("Ошибка генерации QR-кода"));
      }
    } else {
      const svg = svgRef.current;
      if (!svg) return;
      try {
        JsBarcode(svg, val, {
          format,
          lineColor: "#000",
          background: "#fff",
          width: 1.8,
          height: barPreviewH,
          displayValue: true,
          font,
          fontSize: lh <= 30 ? 9 : 11,
          margin: 4,
        });
      } catch {
        const fmt = ALL_FORMATS.find((f) => f.id === format);
        setBarcodeError(`Некорректное значение для ${fmt?.label || format}`);
      }
    }
  }, [format, font, lh, barPreviewH]);

  useEffect(() => {
    if (value) renderBarcode(value);
  }, [value, format, font, labelSize, rotated, hasText, textLines, renderBarcode]);

  // ─── print ─────────────────────────────────────────────────────────────────

  const buildPrintHtml = useCallback((labelW, labelH) => {
    const p = gridParams({ width: labelW, height: labelH }, format, hasText, textLines);
    const matrixImageUrl = format === DATA_MATRIX_FORMAT
      ? dataMatrixDataUrl(value, Math.max(600, p.qrPx))
      : "";

    const labelItems = Array.from({ length: copies }, (_, i) => {
      const barcodeSlot = format === DATA_MATRIX_FORMAT
        ? `<div class="qr-square" style="width:${p.qrMm}mm;height:${p.qrMm}mm;">
             <img class="matrix-img" src="${matrixImageUrl}" alt="" />
           </div>`
        : is2dFormat(format)
          ? `<div class="qr-square" style="width:${p.qrMm}mm;height:${p.qrMm}mm;">
               <canvas id="pc-${i}" width="${p.qrPx}" height="${p.qrPx}"></canvas>
             </div>`
          : `<svg id="ps-${i}" style="display:block;"></svg>`;

      if (p.noText) {
        // Only code, centered
        return `<div class="label label--center"><div class="code-cell code-cell--fixed">${barcodeSlot}</div></div>`;
      }

      const titleHtml = title
        ? `<div class="lbl-title">${title}</div>` : "";
      const datesHtml = [
        dateFields.mfg  && dateValues.mfg            ? `<div class="lbl-date"><span class="dk">Дата пр-ва:</span> <b>${fmtDate(dateValues.mfg)}</b></div>` : "",
        dateFields.shelf && dateValues.shelf.value    ? `<div class="lbl-date"><span class="dk">Срок годн.:</span> <b>${shelfLabel(dateValues.shelf.value, dateValues.shelf.unit)}</b></div>` : "",
        dateFields.exp  && dateValues.exp             ? `<div class="lbl-date"><span class="dk">Годен до:</span> <b>${fmtDate(dateValues.exp)}</b></div>` : "",
      ].filter(Boolean).join("");
      const dateRow = (label, val) => `<div class="lbl-date"><span class="dk">${label}</span><b>${val}</b></div>`;
      const gridDatesHtml = [
        dateFields.mfg && dateValues.mfg ? dateRow("Дата производства", fmtDate(dateValues.mfg)) : "",
        dateFields.shelf && dateValues.shelf.value ? dateRow("Срок годности", shelfLabel(dateValues.shelf.value, dateValues.shelf.unit)) : "",
        dateFields.exp && dateValues.exp ? dateRow("Годен до", fmtDate(dateValues.exp)) : "",
      ].filter(Boolean).join("");

      if (p.horiz) {
        return `<div class="label label--horiz">
          <div class="code-cell code-cell--fixed">${barcodeSlot}</div>
          <div class="text-block">${titleHtml}${gridDatesHtml}</div>
        </div>`;
      }

      return `<div class="label label--portrait">
        <div class="code-cell code-cell--fixed">${barcodeSlot}</div>
        <div class="text-block">${titleHtml}${gridDatesHtml}</div>
      </div>`;
    });

    const pages = labelItems.join("<div class='pb'></div>");

    return `<!DOCTYPE html><html><head>
      <meta charset="utf-8"/>
      <style>
        @page { size: ${labelW} ${labelH}; margin: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: "${font}", sans-serif; width: ${labelW}; }
        .pb { page-break-after: always; }

        .label {
          width: ${labelW}; height: ${labelH};
          padding: ${LABEL_INSET_MM}mm;
          overflow: hidden;
          display: grid;
        }
        .label--center {
          place-items: center;
        }
        .label--horiz {
          grid-template-columns: ${CODE_GRID_FR}fr ${TEXT_GRID_FR}fr;
          grid-template-rows: 1fr;
        }
        .label--portrait {
          grid-template-columns: 1fr;
          grid-template-rows: ${CODE_GRID_FR}fr ${TEXT_GRID_FR}fr;
        }

        .code-cell {
          min-width: 0;
          min-height: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .code-cell--fixed canvas {
          display: block;
          width: 100% !important;
          height: 100% !important;
          image-rendering: pixelated;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        .qr-square {
          flex: 0 0 auto;
          display: block;
          overflow: hidden;
        }
        .matrix-img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: contain;
          image-rendering: pixelated;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }

        .text-block {
          min-width: 0;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          overflow: hidden;
          line-height: 1.08;
        }
        .label--portrait .text-block { align-items: center; text-align: center; }

        .lbl-title {
          font-size: ${p.titleFs}pt;
          font-weight: bold;
          max-width: 100%;
          overflow-wrap: anywhere;
          margin-bottom: 0.2mm;
        }

        svg { max-width: 100%; height: auto; display: block; }

        .lbl-date {
          font-size: ${p.fs}pt;
          max-width: 100%;
          display: flex;
          flex-direction: column;
          overflow-wrap: anywhere;
        }
        .lbl-date b { font-size: ${p.fs}pt; }
        .dk { color: #666; }
      </style>
    </head><body>${pages}</body></html>`;
  }, [format, font, copies, title, dateFields, dateValues, hasText, textLines, value]);

  const openPrintWindow = useCallback((forSave = false) => {
    if (!value) return;
    const html = buildPrintHtml(effectiveW, effectiveH);
    const p    = gridParams({ width: effectiveW, height: effectiveH }, format, hasText, textLines);

    const win = window.open("", "_blank", "width=500,height=400");
    if (!win) return;
    if (forSave) win.document.title = `Этикетка_${value.replace(/\s/g, "_")}`;
    win.document.write(html);
    win.document.close();

    win.onload = () => {
      const tasks = Array.from({ length: copies }, (_, i) => {
        if (format === "qr" || format === DATA_MATRIX_FORMAT) {
          if (format === DATA_MATRIX_FORMAT) return Promise.resolve();
          const c = win.document.getElementById(`pc-${i}`);
          if (!c) return Promise.resolve();
          return QRCode.toCanvas(c, value, { width: Math.max(200, p.qrPx), margin: 1 });
        }
        const s = win.document.getElementById(`ps-${i}`);
        if (s) {
          try {
            JsBarcode(s, value, {
              format, lineColor: "#000", background: "#fff",
              width: 1.8, height: p.barcodeH,
              displayValue: true, font, fontSize: p.fs * 1.2, margin: 3,
            });
          } catch { /* ignore */ }
        }
        return Promise.resolve();
      });
      Promise.all(tasks).then(() => setTimeout(() => { win.print(); win.close(); }, 200));
    };
  }, [value, format, font, effectiveW, effectiveH, copies, buildPrintHtml, hasText, textLines]);

  const triggerPrint = useCallback(() => { if (!isBot()) openPrintWindow(false); }, [openPrintWindow, isBot]);
  const triggerSave  = useCallback(() => { if (!isBot()) openPrintWindow(true);  }, [openPrintWindow, isBot]);

  useEffect(() => {
    if (!autoPrint || !pendingScanPrintRef.current || !value.trim() || barcodeError) return undefined;
    pendingScanPrintRef.current = false;
    const t = setTimeout(() => triggerPrint(), 200);
    return () => clearTimeout(t);
  }, [autoPrint, value, barcodeError, triggerPrint]);

  // ─── USB/HID scanner + keyboard ───────────────────────────────────────────

  const finalizeScannerBuffer = useCallback((shouldPrint = true) => {
    const buf = scannerBuffer.current.filter((ch) => typeof ch === "string");
    scannerBuffer.current = [];
    clearTimeout(scannerTimer.current);
    clearTimeout(scannerFinalizeTimer.current);
    scannerFinalizeTimer.current = null;

    if (buf.length < SCANNER_MIN_LENGTH) return false;

    pendingScanPrintRef.current = autoPrint && shouldPrint;
    setValue(buf.join(""));
    return true;
  }, [autoPrint]);

  const handleKeyDown = useCallback((e) => {
    // GS character (ASCII 29) — sent by USB scanners as GS1 separator
    // Browsers may report it as "GroupSeparator", "Unidentified", or via Ctrl+]
    const isGsChar =
      e.keyCode === 29 ||
      e.key === "GroupSeparator" ||
      e.key === "\x1d" ||
      (e.ctrlKey && e.key === "]" && scannerBuffer.current.length > 0);
    if (isGsChar) {
      e.preventDefault();
      const now = Date.now();
      const last = scannerBuffer.current._lastTime || 0;
      if (now - last < SCANNER_THRESHOLD_MS || scannerBuffer.current.length > 0) {
        scannerBuffer.current.push(GS);
        scannerBuffer.current._lastTime = now;
        clearTimeout(scannerTimer.current);
        scannerTimer.current = setTimeout(() => { scannerBuffer.current = []; }, 500);
      }
      return;
    }

    if (e.key === "Enter" || e.key === "Tab") {
      const buf = scannerBuffer.current;
      e.preventDefault();
      if (buf.length >= SCANNER_MIN_LENGTH) {
        clearTimeout(scannerTimer.current);
        clearTimeout(scannerFinalizeTimer.current);
        scannerFinalizeTimer.current = setTimeout(() => {
          finalizeScannerBuffer(true);
        }, SCANNER_FINALIZE_DELAY_MS);
      } else {
        scannerBuffer.current = [];
        if (e.key === "Enter" && value.trim()) triggerPrint();
      }
      return;
    }
    if (e.key.length === 1) {
      const now  = Date.now();
      const last = scannerBuffer.current._lastTime || 0;
      let continuedAfterSeparator = false;
      if (scannerFinalizeTimer.current && scannerBuffer.current.length >= SCANNER_MIN_LENGTH) {
        clearTimeout(scannerFinalizeTimer.current);
        scannerFinalizeTimer.current = null;
        scannerBuffer.current.push(GS);
        continuedAfterSeparator = true;
      }
      if (continuedAfterSeparator || now - last < SCANNER_THRESHOLD_MS || scannerBuffer.current.length === 0) {
        scannerBuffer.current.push(e.key);
      } else {
        scannerBuffer.current = [e.key];
      }
      scannerBuffer.current._lastTime = now;
      clearTimeout(scannerTimer.current);
      scannerTimer.current = setTimeout(() => { scannerBuffer.current = []; }, 500);
    }
  }, [triggerPrint, value, finalizeScannerBuffer]);

  const canAct = value.trim().length > 0 && !barcodeError;

  // ─── preview date rows (reused in multiple layouts) ────────────────────────

  const PreviewDates = () => (
    <>
      {dateFields.mfg && dateValues.mfg && (
        <div className="bl__preview-date">
          <span className="bl__preview-date-key">Дата производства</span>
          <span className="bl__preview-date-val">{fmtDate(dateValues.mfg)}</span>
        </div>
      )}
      {dateFields.shelf && dateValues.shelf.value && (
        <div className="bl__preview-date">
          <span className="bl__preview-date-key">Срок годности</span>
          <span className="bl__preview-date-val">{shelfLabel(dateValues.shelf.value, dateValues.shelf.unit)}</span>
        </div>
      )}
      {dateFields.exp && dateValues.exp && (
        <div className="bl__preview-date">
          <span className="bl__preview-date-key">Годен до</span>
          <span className="bl__preview-date-val">{fmtDate(dateValues.exp)}</span>
        </div>
      )}
    </>
  );

  // ─── render ────────────────────────────────────────────────────────────────

  return (
    <div className="bl">

      {/* ── camera overlay ── */}
      {camera.open && (
        <div className="bl__cam-overlay" onClick={camera.stopScan}>
          <div className="bl__cam-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bl__cam-header">
              <span>Наведите камеру на штрихкод или QR</span>
              <div className="bl__cam-actions">
                {camera.devices.length > 1 && (
                  <button
                    className="bl__cam-switch"
                    onClick={camera.switchCamera}
                    type="button"
                    title="Сменить камеру"
                    aria-label="Сменить камеру"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 0 1-15.36 6.36L3 15"/>
                      <path d="M3 21v-6h6"/>
                      <path d="M3 12a9 9 0 0 1 15.36-6.36L21 9"/>
                      <path d="M21 3v6h-6"/>
                    </svg>
                  </button>
                )}
                <button className="bl__cam-close" onClick={camera.stopScan} aria-label="Закрыть">✕</button>
              </div>
            </div>
            <div className={`bl__cam-viewport${isMatrix ? " bl__cam-viewport--matrix" : ""}`}>
              <video ref={camera.videoRef} className="bl__cam-video" autoPlay playsInline muted />
              <div className="bl__cam-crosshair" />
              {camera.scanned && (
                <div className="bl__cam-result">
                  <span className="bl__cam-check">✓</span> {camera.scanned}
                </div>
              )}
            </div>
            {camera.error && <div className="bl__cam-error">{camera.error}</div>}
            <div className="bl__cam-hint">Нажмите за пределами окна, чтобы закрыть</div>
          </div>
        </div>
      )}

      {/* ── controls ── */}
      <div className="bl__controls">

        {/* Format */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">Формат штрихкода</label>
          <div className="bl__format-grid">
            {MAIN_FORMATS.map((f) => (
              <button key={f.id}
                className={`bl__fmt-btn${format === f.id ? " bl__fmt-btn--active" : ""}`}
                onClick={() => setFormat(f.id)}>
                <span className="bl__fmt-tag">{f.group}</span>{f.label}
              </button>
            ))}
            <button
              className={`bl__fmt-btn bl__fmt-btn--more${showOther ? " bl__fmt-btn--more-open" : ""}`}
              onClick={() => setShowOther((v) => !v)}>
              Другие {showOther ? "▲" : "▼"}
            </button>
          </div>
          {showOther && (
            <div className="bl__format-grid bl__format-other">
              {OTHER_FORMATS.map((f) => (
                <button key={f.id}
                  className={`bl__fmt-btn${format === f.id ? " bl__fmt-btn--active" : ""}`}
                  onClick={() => { setFormat(f.id); setShowOther(false); }}>
                  <span className="bl__fmt-tag">{f.group}</span>{f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Label size + rotate + copies */}
        <div className="bl__row" style={{ gap: 12 }}>
          <div className="bl__row bl__row--col" style={{ flex: 1 }}>
            <label className="bl__label">Этикетка</label>
            <div className="bl__size-row">
              <select className="bl__select" value={labelSize.id}
                onChange={(e) => { setLabelSize(LABEL_SIZES.find((s) => s.id === e.target.value)); setRotated(false); }}>
                {LABEL_SIZES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <button
                className={`bl__rotate-btn${rotated ? " bl__rotate-btn--on" : ""}`}
                onClick={() => setRotated((v) => !v)}
                title={rotated ? "Альбомная → книжная" : "Книжная → альбомная"}
                type="button"
                aria-label="Повернуть этикетку"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 4v6h6"/>
                  <path d="M3.51 15a9 9 0 1 0 .49-4.25"/>
                </svg>
              </button>
            </div>
          </div>
          <div className="bl__row bl__row--col">
            <label className="bl__label">Копий</label>
            <input className="bl__input bl__input--copies" type="number" min={1} max={100} value={copies}
              onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))} />
          </div>
        </div>

        {/* Font */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">Шрифт текста</label>
          <select className="bl__select" value={font}
            onChange={(e) => setFont(e.target.value)} style={{ fontFamily: font }}>
            {FONTS.map((f) => (
              <option key={f.id} value={f.id} style={{ fontFamily: f.id }}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Value */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">Значение</label>
          <div className="bl__input-wrap">
            <input
              ref={inputRef}
              className="bl__input bl__input--value"
              type="text"
              placeholder="Введите или поднесите сканер..."
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
            <button className="bl__cam-btn" onClick={camera.startScan}
              title="Сканировать через камеру" type="button" aria-label="Открыть сканер камеры">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                <circle cx="12" cy="13" r="4"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Title */}
        <div className="bl__row bl__row--col">
          <label className="bl__label">
            Название <span className="bl__optional">(опционально)</span>
          </label>
          <input className="bl__input" type="text" placeholder="Название товара..."
            value={title} onChange={(e) => setTitle(e.target.value)} style={{ fontFamily: font }} />
        </div>

        {/* Dates */}
        <div className="bl__date-section">
          <div className="bl__date-header">Даты на этикетке</div>

          <div className="bl__date-row">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={dateFields.mfg} onChange={() => toggleDateField("mfg")} />
              Дата производства
            </label>
            {dateFields.mfg && (
              <input className="bl__input bl__input--date" type="date"
                value={dateValues.mfg} onChange={(e) => handleMfgChange(e.target.value)} />
            )}
          </div>

          <div className="bl__date-row">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={dateFields.shelf} onChange={() => toggleDateField("shelf")} />
              Срок годности
              {dateFields.shelf && !dateManual.shelf && (dateFields.mfg || dateFields.exp) && (
                <span className="bl__auto-tag">авто</span>
              )}
            </label>
            {dateFields.shelf && (
              <div className="bl__shelf-row">
                <input
                  className={`bl__input bl__input--shelf-num${!dateManual.shelf ? " bl__input--auto" : ""}`}
                  type="number" min={1} placeholder="число"
                  value={dateValues.shelf.value}
                  onChange={(e) => handleShelfValChange(e.target.value)}
                />
                <select className="bl__select bl__select--shelf"
                  value={dateValues.shelf.unit} onChange={(e) => handleShelfUnitChange(e.target.value)}>
                  {SHELF_UNITS.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="bl__date-row">
            <label className="bl__checkbox-label">
              <input type="checkbox" checked={dateFields.exp} onChange={() => toggleDateField("exp")} />
              Годен до
              {dateFields.exp && !dateManual.exp && (dateFields.mfg || dateFields.shelf) && (
                <span className="bl__auto-tag">авто</span>
              )}
            </label>
            {dateFields.exp && (
              <input
                className={`bl__input bl__input--date${!dateManual.exp ? " bl__input--auto" : ""}`}
                type="date" value={dateValues.exp}
                onChange={(e) => handleExpChange(e.target.value)} />
            )}
          </div>

          {dateInconsistent && (
            <div className="bl__date-warning">
              Даты не сходятся: {fmtDate(dateValues.mfg)} + {shelfLabel(dateValues.shelf.value, dateValues.shelf.unit)} ≠ {fmtDate(dateValues.exp)}
            </div>
          )}
        </div>

        <HoneypotField />
        {barcodeError && <div className="bl__error">{barcodeError}</div>}

        <label className="bl__checkbox-label bl__autoprint-toggle">
          <input
            type="checkbox"
            checked={autoPrint}
            onChange={(e) => setAutoPrint(e.target.checked)}
          />
          Включить автопечать после сканирования
        </label>

        <div className="bl__actions">
          <button className="bl__print-btn" onClick={triggerPrint} disabled={!canAct}>
            Печать{copies > 1 ? ` (${copies} шт.)` : ""}
          </button>
          <button className="bl__save-btn" onClick={triggerSave} disabled={!canAct}
            title="В диалоге выберите «Сохранить как PDF»">
            Сохранить PDF
          </button>
          <button className="bl__clear-btn"
            onClick={() => { setValue(""); setTitle(""); setBarcodeError(""); inputRef.current?.focus(); }}>
            ✕
          </button>
        </div>
        <div className="bl__hint">Enter — печать сразу. Сканер — авто-печать при считывании.</div>
      </div>

      {/* ── preview ── */}
      <div className="bl__preview">
        <div className="bl__preview-label">Превью этикетки</div>
        <div
          ref={previewBoxRef}
          className="bl__preview-box"
          style={{ aspectRatio: `${lw} / ${lh}` }}
        >
          {!value && <span className="bl__preview-hint">Введите значение</span>}

          {value && (
            <>
              {/* ── 1: QR без текста — центр ── */}
              {qrNoText && (
                <div className="bl__preview-inner bl__preview-inner--center" style={{ padding: previewPadPx }}>
                  <div className="bl__qr-square" style={{ width: qrNoTextPx, height: qrNoTextPx }}>
                    <canvas ref={canvasRef} width={256} height={256} />
                  </div>
                </div>
              )}

              {/* ── 2: QR + текст, landscape — QR влево ── */}
              {qrHoriz && (
                <div
                  className="bl__preview-inner bl__preview-inner--horiz"
                  style={{
                    fontFamily: font,
                    padding: previewPadPx,
                    display: "grid",
                    gridTemplateColumns: `${CODE_GRID_FR}fr ${TEXT_GRID_FR}fr`,
                    gridTemplateRows: "1fr",
                    gap: 0,
                  }}
                >
                  <div className="bl__preview-code-cell">
                    <div className="bl__qr-square" style={{ width: qrHorizPx, height: qrHorizPx }}>
                      <canvas ref={canvasRef} width={256} height={256} />
                    </div>
                  </div>
                  <div className="bl__preview-text" style={{ fontSize: previewTextFs }}>
                    {title && <div className="bl__preview-title" style={{ fontSize: Math.round(previewTextFs * 1.08) }}>{title}</div>}
                    <PreviewDates />
                  </div>
                </div>
              )}

              {/* ── 3: QR + текст, portrait — QR сверху ── */}
              {qrPortrait && (
                <div
                  className="bl__preview-inner bl__preview-inner--portrait"
                  style={{
                    fontFamily: font,
                    padding: previewPadPx,
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gridTemplateRows: `${CODE_GRID_FR}fr ${TEXT_GRID_FR}fr`,
                    gap: 0,
                  }}
                >
                  <div className="bl__preview-code-cell">
                    <div className="bl__qr-square" style={{ width: qrPortraitPx, height: qrPortraitPx }}>
                      <canvas ref={canvasRef} width={256} height={256} />
                    </div>
                  </div>
                  <div className="bl__preview-text bl__preview-text--below" style={{ fontSize: previewTextFs }}>
                    {title && <div className="bl__preview-title" style={{ fontSize: Math.round(previewTextFs * 1.08) }}>{title}</div>}
                    <PreviewDates />
                  </div>
                </div>
              )}

              {/* ── 4: 1D штрихкод ── */}
              {!isMatrix && (
                <div
                  className="bl__preview-inner bl__preview-inner--portrait"
                  style={{ fontFamily: font, padding: previewPadPx }}
                >
                  <svg ref={svgRef} className="bl__svg" />
                  {hasText && (
                    <div className="bl__preview-text bl__preview-text--below" style={{ fontSize: previewTextFs }}>
                      {title && <div className="bl__preview-title" style={{ fontSize: Math.round(previewTextFs * 1.08) }}>{title}</div>}
                      <PreviewDates />
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="bl__preview-size">{labelSize.label}</div>
      </div>
    </div>
  );
}
